import * as React from "react";

interface AnimatedRankingItemProps {
	children: React.ReactNode;
	className?: string;
	delay?: number;
}

export function AnimatedRankingItem({
	children,
	className,
	delay = 0,
}: AnimatedRankingItemProps) {
	const itemRef = React.useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = React.useState(false);
	const [scale, setScale] = React.useState(0);
	const [opacity, setOpacity] = React.useState(0);

	React.useEffect(() => {
		const element = itemRef.current;
		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 },
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, []);

	React.useEffect(() => {
		if (!isVisible) return;

		const startTime = performance.now();
		const duration = 600;
		const startScale = 0;
		const endScale = 1;

		function animate(currentTime: number) {
			const elapsed = currentTime - startTime - delay;
			if (elapsed < 0) {
				requestAnimationFrame(animate);
				return;
			}

			const progress = Math.min(elapsed / duration, 1);

			const easeOutBack = (t: number): number => {
				const c1 = 1.70158;
				const c3 = c1 + 1;
				return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
			};

			const easedProgress = easeOutBack(progress);
			const currentScale = startScale + (endScale - startScale) * easedProgress;
			const currentOpacity = progress;

			setScale(currentScale);
			setOpacity(currentOpacity);

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				setScale(endScale);
				setOpacity(1);
			}
		}

		requestAnimationFrame(animate);
	}, [isVisible, delay]);

	return (
		<div
			ref={itemRef}
			className={className}
			style={{
				transform: `scale(${scale})`,
				opacity,
			}}
		>
			{children}
		</div>
	);
}
